// ===========================================
// Class Message Modal
// ===========================================
// Allows a teacher/admin to message class members from the lesson detail page.
// Modes: Whole Group, Individual, Multiple select.

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Group as GroupIcon, Person as PersonIcon, People as PeopleIcon } from '@mui/icons-material';
import { ClassMember } from '../../../api/lessons.api';

// ===========================================
// TYPES
// ===========================================

type SendMode = 'group' | 'individual' | 'multi';

interface ClassMessageModalProps {
  open: boolean;
  lessonName: string;
  /**
   * Heading and empty-state wording. Defaults to the class phrasing.
   *
   * The modal is reused verbatim by the admin Families page ("Message family"), which is
   * why the copy is a prop rather than a hardcoded string — the picker, validation and
   * send modes are identical, and only the noun differs.
   */
  title?: string;
  emptyMessage?: string;
  /**
   * Qualifier after the student count. Classes only list students who have a login
   * ("with accounts"); families list any student with an email, so the family surface
   * passes an empty string rather than claiming something untrue.
   */
  studentQualifier?: string;
  parents: ClassMember[];
  students: ClassMember[];
  sending: boolean;
  /**
   * Recipients still being fetched.
   *
   * Without this the modal cannot tell "no recipients" from "not loaded yet", so it
   * flashed the empty state ("nobody to message") for the length of the request before
   * the form appeared — alarming, and untrue.
   */
  loading?: boolean;
  /**
   * The recipient fetch failed.
   *
   * Distinct from `loading` and from genuinely-empty: without it, a failed request looks
   * identical to "this family has no contacts", because React Query leaves `data`
   * undefined and the call sites default to `[]`. Telling an admin to go add an email
   * that already exists is worse than saying nothing.
   */
  loadError?: boolean;
  onClose: () => void;
  /** Called with each recipientId and the message content. May be called multiple times. */
  onSend: (recipientIds: string[], content: string) => void;
}

// ===========================================
// HELPERS
// ===========================================

function getInitials(firstName: string, lastName: string) {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function MemberAvatar({ member }: { member: ClassMember }) {
  const color = member.role === 'PARENT' ? '#4580E4' : '#96DAC9';
  return (
    <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: '0.875rem' }}>
      {getInitials(member.firstName, member.lastName)}
    </Avatar>
  );
}

function MemberSubtext({ member }: { member: ClassMember }) {
  if (member.role === 'PARENT' && member.studentNames?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Parent of {member.studentNames.join(', ')}
      </Typography>
    );
  }
  if (member.role === 'STUDENT' && member.studentName) {
    return (
      <Typography variant="caption" color="text.secondary">
        Student
      </Typography>
    );
  }
  return null;
}

// ===========================================
// COMPONENT
// ===========================================

export default function ClassMessageModal({
  open,
  lessonName,
  title = 'Message Class',
  studentQualifier = ' with accounts',
  emptyMessage = 'No enrolled students with family accounts found in this class.',
  parents,
  students,
  sending,
  loading = false,
  loadError = false,
  onClose,
  onSend,
}: ClassMessageModalProps) {
  const [mode, setMode] = useState<SendMode>('group');
  const [includeStudents, setIncludeStudents] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [content, setContent] = useState('');

  const handleClose = () => {
    if (sending) return;
    setMode('group');
    setIncludeStudents(false);
    setSelectedIds(new Set());
    setContent('');
    onClose();
  };

  // Members shown in the list (individual/multi modes)
  const allMembers = useMemo(() => {
    return [...parents, ...(includeStudents ? students : [])];
  }, [parents, students, includeStudents]);

  // Recipients for whole-group send
  const groupRecipientIds = useMemo(() => {
    return [...parents, ...(includeStudents ? students : [])].map((m) => m.id);
  }, [parents, students, includeStudents]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const recipientCount =
    mode === 'group'
      ? groupRecipientIds.length
      : mode === 'individual'
      ? selectedIds.size > 0 ? 1 : 0
      : selectedIds.size;

  const canSend =
    content.trim().length > 0 &&
    content.length <= 5000 &&
    recipientCount > 0 &&
    !sending;

  const handleSend = () => {
    if (!canSend) return;
    const ids =
      mode === 'group'
        ? groupRecipientIds
        : Array.from(selectedIds);
    onSend(ids, content.trim());
  };

  const noMembers = !loading && parents.length === 0 && students.length === 0;

  return (
    <Dialog open={open} onClose={sending ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="span">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {lessonName}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={sending} sx={{ ml: 2 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          // Show progress, never the empty state, while recipients are still loading —
          // "nobody to message" is a claim we cannot make until the fetch resolves.
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">
            Could not load the recipients. Close this and try again.
          </Alert>
        ) : noMembers ? (
          <Alert severity="info">{emptyMessage}</Alert>
        ) : (
          <>
            {/* Send mode selector */}
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, val) => {
                if (val) {
                  setMode(val);
                  setSelectedIds(new Set());
                }
              }}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="group">
                <GroupIcon fontSize="small" sx={{ mr: 0.75 }} />
                Whole Group
              </ToggleButton>
              <ToggleButton value="individual">
                <PersonIcon fontSize="small" sx={{ mr: 0.75 }} />
                Individual
              </ToggleButton>
              <ToggleButton value="multi">
                <PeopleIcon fontSize="small" sx={{ mr: 0.75 }} />
                Multiple
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Include students toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="body2">
                  {mode === 'group'
                    ? `Recipients: ${groupRecipientIds.length} ${groupRecipientIds.length === 1 ? 'person' : 'people'}`
                    : mode === 'individual'
                    ? 'Select one person'
                    : `Selected: ${selectedIds.size}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {parents.length} parent{parents.length !== 1 ? 's' : ''}
                  {students.length > 0 && `, ${students.length} student${students.length !== 1 ? 's' : ''}${studentQualifier}`}
                </Typography>
              </Box>
              {students.length > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeStudents}
                      onChange={(e) => {
                        setIncludeStudents(e.target.checked);
                        setSelectedIds(new Set());
                      }}
                    />
                  }
                  label={<Typography variant="caption">Include students</Typography>}
                  sx={{ mr: 0 }}
                />
              )}
            </Box>

            {/* Member list for individual/multi modes */}
            {mode !== 'group' && (
              <>
                <Divider sx={{ mb: 1 }} />
                {allMembers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No members to select.
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto', mb: 1.5 }}>
                    {allMembers.map((member) => {
                      const isSelected = selectedIds.has(member.id);
                      const isDisabled = mode === 'individual' && selectedIds.size > 0 && !isSelected;
                      return (
                        <ListItem key={member.id} disablePadding>
                          <ListItemButton
                            dense
                            onClick={() => {
                              if (mode === 'individual') {
                                setSelectedIds(isSelected ? new Set() : new Set([member.id]));
                              } else {
                                toggleSelection(member.id);
                              }
                            }}
                            disabled={isDisabled}
                            selected={isSelected}
                            sx={{ borderRadius: 1 }}
                          >
                            {mode === 'multi' && (
                              <Checkbox
                                edge="start"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                                size="small"
                              />
                            )}
                            <ListItemAvatar sx={{ minWidth: 48 }}>
                              <MemberAvatar member={member} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography variant="body2">
                                    {member.firstName} {member.lastName}
                                  </Typography>
                                  <Chip
                                    label={member.role === 'PARENT' ? 'Parent' : 'Student'}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.65rem',
                                      bgcolor: member.role === 'PARENT' ? 'primary.main' : 'success.main',
                                      color: 'white',
                                    }}
                                  />
                                </Box>
                              }
                              secondary={<MemberSubtext member={member} />}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {/* Message input */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Type your message..."
              disabled={sending}
              error={content.length > 5000}
              helperText={content.length > 4500 ? `${5000 - content.length} characters remaining` : ''}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
        {!noMembers && !loading && !loadError && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!canSend}
            startIcon={sending ? <CircularProgress size={16} /> : undefined}
          >
            {sending
              ? 'Sending...'
              : `Send to ${recipientCount} ${recipientCount === 1 ? 'person' : 'people'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
